import { Field } from '@amore/ui/components/field'
import { FormLayout } from '@amore/ui/components/form-layout'
import { Input } from '@amore/ui/components/input'
import { Select, SelectItem } from '@amore/ui/components/select'
import { TimePicker } from '@amore/ui/components/time-picker'
import { Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { usePrototype } from '@/mock/store'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { CeremonyFieldLabel, editorSections, softControl } from './editor-fields'

export function CeremonyPlanBSheet({ onClose }: { onClose: () => void }) {
  const { data, canEdit, update } = usePrototype()
  const { showToast } = useToast()
  const initial = data.ceremony.planB
  const [location, setLocation] = useState(initial?.location ?? '')
  const [person, setPerson] = useState(initial?.decisionByMemberId ?? null)
  const [time, setTime] = useState(initial?.decisionTime ?? '')
  const [error, setError] = useState<string>()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const form = useRef<HTMLFormElement>(null)
  const timeRef = useRef<HTMLInputElement>(null)
  const dirty =
    canEdit &&
    (location !== (initial?.location ?? '') ||
      person !== (initial?.decisionByMemberId ?? null) ||
      time !== (initial?.decisionTime ?? ''))
  const remove = () => {
    if (!canEdit) return
    update((current) => ({ ...current, ceremony: { ...current.ceremony, planB: null } }))
    showToast({ title: 'Plán B odebrán' })
    onClose()
  }
  return (
    <>
      <DetailSheet
        open
        eyebrow="Místo a příprava"
        title="Plán B"
        dirty={dirty}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        primaryAction={
          canEdit ? { label: 'Uložit', onClick: () => form.current?.requestSubmit() } : undefined
        }
        destructiveAction={
          canEdit && initial
            ? {
                label: 'Odebrat plán B',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmDelete(true),
              }
            : undefined
        }
      >
        <FormLayout
          ref={form}
          noValidate
          className={editorSections}
          onSubmit={(event) => {
            event.preventDefault()
            if (!canEdit) return
            if (time && !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
              setError('Zadejte čas ve formátu HH:mm, například 12:00.')
              timeRef.current?.focus()
              return
            }
            const planB =
              location.trim() || person || time
                ? {
                    location: location.trim(),
                    decisionByMemberId: person,
                    decisionTime: time || null,
                  }
                : null
            update((current) => ({ ...current, ceremony: { ...current.ceremony, planB } }))
            showToast({ title: 'Plán B uložen', tone: 'success' })
            onClose()
          }}
        >
          <Field
            controlId="ceremony-plan-b-location"
            label={<CeremonyFieldLabel>Náhradní místo</CeremonyFieldLabel>}
          >
            <Input
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              readOnly={!canEdit}
              maxLength={300}
              variant="soft"
              className={softControl}
              placeholder="Zatím nevybrané"
            />
          </Field>
          <Field
            controlId="ceremony-plan-b-person"
            label={<CeremonyFieldLabel>Kdo rozhodne</CeremonyFieldLabel>}
          >
            <Select
              value={person ?? 'none'}
              onValueChange={(value) => setPerson(value === 'none' ? null : value)}
              disabled={!canEdit}
              variant="soft"
              className={softControl}
            >
              <SelectItem value="none">Zatím neurčeno</SelectItem>
              {person && !data.members.some((member) => member.id === person) ? (
                <SelectItem value={person}>Dříve vybraný člen týmu</SelectItem>
              ) : null}
              {data.members.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.name}
                </SelectItem>
              ))}
            </Select>
          </Field>
          <Field
            controlId="ceremony-plan-b-time"
            label={<CeremonyFieldLabel>Kdy rozhodne</CeremonyFieldLabel>}
            error={error}
          >
            <TimePicker
              ref={timeRef}
              value={time}
              onValueChange={(value) => {
                setTime(value)
                setError(undefined)
              }}
              readOnly={!canEdit}
              variant="soft"
              className={softControl}
              placeholder="Zatím neurčeno"
              labels={{
                open: 'Vybrat čas rozhodnutí',
                title: 'Čas rozhodnutí',
                close: 'Zavřít výběr času',
                hours: 'Hodiny',
                minutes: 'Minuty',
                clear: 'Bez času',
                confirm: 'Potvrdit čas',
                format: '24hodinový formát · HH:mm',
              }}
            />
          </Field>
        </FormLayout>
      </DetailSheet>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        heading="Odebrat plán B?"
        description="Náhradní místo, člověk i čas rozhodnutí se vymažou."
        keepLabel="Ponechat plán B"
        confirmLabel="Odebrat plán B"
        onConfirm={remove}
      />
    </>
  )
}
